import AttributeModel from '@mm/state/model/AttributeModel';
import PageGroupModel from '@mm/state/model/PageGroupModel';
import { HiddenPageKinds, PageKind } from '@mm/state/model/PageModel';
import { DropdownOption } from '@mm/components/legacy-components/PrimarySelect/PrimarySelect';

type EntityLike = {
  id: number;
  name: string;
  [key: string]: any;
};

const groupPages = (pages, pageGroupLabels, moveNoOptionUp = false) => {
  const OLD_PATHS_FOLDER_NAME = 'Converted from Paths';
  const convertedFromPathsGroupId = Object.entries(pageGroupLabels).find(
    entry => entry[1] === OLD_PATHS_FOLDER_NAME,
  )?.[0];

  const convertedFromPathsPages: DropdownOption[] = [];
  const otherPages: DropdownOption[] = [];

  const noOption = moveNoOptionUp ? pages.find(p => p.id === -1) : undefined;

  pages.forEach(p => {
    if (String(p.pageGroupId) === String(convertedFromPathsGroupId)) {
      convertedFromPathsPages.push(p);
    } else {
      noOption
        ? noOption.id !== p.id && otherPages.push(p)
        : otherPages.push(p);
    }
  });

  const initialPageGroups = noOption
    ? [
        { ...noOption, suggestions: [noOption] },
        {
          title: 'Bot Basics',
          pageGroupId: 0,
          id: 0,
          suggestions: [],
        },
      ]
    : [
        {
          title: 'Bot Basics',
          pageGroupId: 0,
          id: 0,
          suggestions: [],
        },
      ];

  const groupedPages = otherPages.reduce((grouped, item) => {
    if (
      ![
        null,
        PageKind.OnboardingWelcomePage,
        PageKind.StartBot,
        PageKind.OTNPage,
        PageKind.SMSReplyPage,
      ].some(k => item.kind === k)
    ) {
      grouped[noOption ? 1 : 0].suggestions.push(item);
    } else {
      const title =
        item.pageGroupId && pageGroupLabels[item.pageGroupId]
          ? pageGroupLabels[item.pageGroupId]
          : 'Not in Folder';
      const index = (grouped as typeof initialPageGroups).findIndex(
        group => group.pageGroupId === item.pageGroupId,
      );

      if (index !== -1) {
        const pageGroup = grouped[index];
        pageGroup.suggestions.push(item);
      } else {
        grouped.push({
          title,
          pageGroupId: item.pageGroupId,
          id: item.pageGroupId,
          suggestions: [item],
        });
      }
    }

    return grouped;
  }, initialPageGroups);

  const convertedFromPathsPageGroups =
    convertedFromPathsGroupId && !!convertedFromPathsPages.length
      ? [
          {
            title: pageGroupLabels[convertedFromPathsGroupId],
            pageGroupId: convertedFromPathsPages[0].pageGroupId,
            id: convertedFromPathsPages[0].pageGroupId,
            suggestions: convertedFromPathsPages,
          },
        ]
      : [];

  return [...groupedPages, ...convertedFromPathsPageGroups];
};

export const generateBasicStructure = (items, stringValue = null) => {
  const noneValue = items.find(item => item.id === null);
  const itemsGroups = [
    {
      title: '',
      suggestions: items,
    },
  ];
  if (!noneValue && stringValue) {
    itemsGroups.unshift({
      title: '',
      suggestions: [{ id: null, name: stringValue }],
    });
  }
  return itemsGroups;
};

export const nameById = <T extends EntityLike>(
  selectedId: number | string,
  items: T[],
  noValueString = 'No Dialogue',
  couldBeEmpty?: boolean,
) => {
  const defaultValue = couldBeEmpty ? '' : noValueString;
  let selectedItem;

  if (selectedId && items && items !== null && items.length) {
    selectedItem = items.find(item => item.id === Number(selectedId));
  }

  return selectedItem?.name ? selectedItem?.name : defaultValue;
};

export const generatePageStructure = (
  pages,
  pageGroups: PageGroupModel[],
  moveNoOptionUp = false,
) => {
  const filteredPages = pages.filter(
    page => !HiddenPageKinds.includes(page.kind),
  );

  const pageGroupLabels = pageGroups.reduce(
    (sum, item) => ({
      ...sum,
      [item.id]: item.name,
    }),
    {},
  );

  return groupPages(filteredPages, pageGroupLabels, moveNoOptionUp);
};

export const nameAttributes = ['first name', 'last name', 'full name'];

export const generateAttributeStructure = (
  attributes: AttributeModel[],
  fbPageName: string,
  showOnlyNameAttributes: boolean = false,
) => {
  if (showOnlyNameAttributes) {
    const filteredAttributes = attributes.filter(attribute =>
      nameAttributes.some(nameAttribute => nameAttribute === attribute.name),
    );
    return [
      {
        title: '',
        suggestions: filteredAttributes,
      },
    ];
  }
  const systemAttributes = attributes.filter(attribute => attribute.system);
  const customAttributes = attributes.filter(attribute => !attribute.system);
  return [
    {
      title: 'System Attributes',
      suggestions: systemAttributes,
    },
    {
      title: `${fbPageName} Attributes`,
      suggestions: customAttributes,
    },
  ];
};
